import axios from "axios";

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;
const STORAGE_KEY = "networkAccessToken";
const TALENT_API_BASE = `${BASE_URL}/talent`;
export const NETWORK_PORTAL_AUTH_CHANGED_EVENT = "network-portal-auth-changed";

function notifyNetworkPortalAuthChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(NETWORK_PORTAL_AUTH_CHANGED_EVENT));
  }
}

export function getNetworkPortalToken() {
  return localStorage.getItem(STORAGE_KEY);
}

export function setNetworkPortalToken(token) {
  if (token) {
    localStorage.setItem(STORAGE_KEY, token);
  }
  notifyNetworkPortalAuthChanged();
}

export function clearNetworkPortalToken() {
  localStorage.removeItem(STORAGE_KEY);
  notifyNetworkPortalAuthChanged();
}

function authHeaders() {
  return {
    headers: {
      Authorization: `Bearer ${getNetworkPortalToken() || ""}`,
    },
  };
}

function multipartAuthHeaders() {
  return {
    headers: {
      Authorization: `Bearer ${getNetworkPortalToken() || ""}`,
      "Content-Type": "multipart/form-data",
    },
  };
}

function unwrap(response) {
  return response?.data || {};
}

export async function validateNetworkPortalInviteToken(token) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/auth/validate-token`, { token }));
}

export async function loginNetworkPortalMember(email, password) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/auth/login`, { email, password }));
}

export async function requestNetworkPortalPasswordReset(email) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/auth/forgot-password`, { email }));
}

export async function resetNetworkPortalPassword(token, password) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/auth/reset-password`, { token, password }));
}

export async function changeRequiredNetworkPortalPassword({ currentPassword, password, confirmPassword }) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/auth/change-password`, { currentPassword, password, confirmPassword }, authHeaders()));
}

export async function verifyNetworkPortalAccount(token, password) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/auth/verify-account`, { token, password }));
}

export async function logoutNetworkPortalMember() {
  return unwrap(await axios.post(`${TALENT_API_BASE}/auth/logout`, {}, authHeaders()));
}

export async function getCurrentNetworkPortalMember() {
  return unwrap(await axios.get(`${TALENT_API_BASE}/auth/me`, authHeaders()));
}

export async function listNetworkPortalAssignments(params = {}) {
  return unwrap(await axios.get(`${TALENT_API_BASE}/assignments`, { ...authHeaders(), params }));
}

export async function getNetworkPortalAssignment(assignmentId) {
  return unwrap(await axios.get(`${TALENT_API_BASE}/assignments/${assignmentId}`, authHeaders()));
}

export async function acceptNetworkPortalAssignment(assignmentId) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/assignments/${assignmentId}/accept`, {}, authHeaders()));
}

export async function declineNetworkPortalAssignment(assignmentId, reason) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/assignments/${assignmentId}/decline`, { reason }, authHeaders()));
}

export async function startNetworkPortalAssignment(assignmentId) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/assignments/${assignmentId}/start`, {}, authHeaders()));
}

export async function updateNetworkPortalAssignmentStatus(assignmentId, data = {}) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/assignments/${assignmentId}/status`, data, authHeaders()));
}

export async function listNetworkPortalOffers(params = {}) {
  return unwrap(await axios.get(`${TALENT_API_BASE}/offers`, { ...authHeaders(), params }));
}

export async function getNetworkPortalOffer(offerId) {
  return unwrap(await axios.get(`${TALENT_API_BASE}/offers/${offerId}`, authHeaders()));
}

export async function markNetworkPortalOfferViewed(offerId) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/offers/${offerId}/viewed`, {}, authHeaders()));
}

export async function acceptNetworkPortalOffer(offerId) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/offers/${offerId}/accept`, {}, authHeaders()));
}

export async function respondNetworkPortalOffer(offerId, data = {}) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/offers/${offerId}/respond`, data, authHeaders()));
}

export async function declineNetworkPortalOffer(offerId, reason) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/offers/${offerId}/decline`, { reason }, authHeaders()));
}

export async function addNetworkPortalMessage(workRequestId, data = {}) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/work-orders/${workRequestId}/messages`, data, authHeaders()));
}

export async function uploadNetworkPortalMessageAttachments(workRequestId, files = [], data = {}) {
  const formData = new FormData();
  safeFileList(files).forEach((file) => {
    formData.append("files", file);
  });
  if (data.assignmentId != null) {
    formData.append("assignmentId", data.assignmentId);
  }

  return unwrap(
    await axios.post(
      `${TALENT_API_BASE}/work-orders/${workRequestId}/message-attachments`,
      formData,
      multipartAuthHeaders()
    )
  );
}

export async function markNetworkPortalMessagesRead(workRequestId, data = {}) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/work-orders/${workRequestId}/messages/mark-read`, data, authHeaders()));
}

export async function getNetworkPortalMessageAttachment(messageId, fileId) {
  return unwrap(await axios.get(`${TALENT_API_BASE}/messages/${messageId}/attachments/${fileId}/download`, authHeaders()));
}

export async function createNetworkPortalSubmission(workRequestId, data = {}) {
  return unwrap(await axios.post(`${TALENT_API_BASE}/work-orders/${workRequestId}/submissions`, data, authHeaders()));
}

export async function uploadNetworkPortalAssignmentFiles(assignmentId, files = []) {
  const formData = new FormData();
  safeFileList(files).forEach((file) => {
    formData.append("files", file);
  });

  return unwrap(
    await axios.post(
      `${TALENT_API_BASE}/assignments/${assignmentId}/files`,
      formData,
      multipartAuthHeaders()
    )
  );
}

export async function createNetworkPortalAssignmentSubmission(assignmentId, data = {}) {
  const uploadFiles = safeFileList(data.files);
  if (uploadFiles.length) {
    const formData = new FormData();
    uploadFiles.forEach((file) => {
      formData.append("files", file);
    });
    if (data.notes != null) {
      formData.append("notes", data.notes);
    }

    return unwrap(
      await axios.post(
        `${TALENT_API_BASE}/assignments/${assignmentId}/submissions`,
        formData,
        multipartAuthHeaders()
      )
    );
  }

  return unwrap(
    await axios.post(
      `${TALENT_API_BASE}/assignments/${assignmentId}/submissions`,
      {
        filesJson: Array.isArray(data.filesJson) ? data.filesJson : [],
        notes: data.notes || null,
      },
      authHeaders()
    )
  );
}

function safeFileList(files) {
  if (!files) return [];
  return Array.isArray(files) ? files.filter(Boolean) : Array.from(files).filter(Boolean);
}
